import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  SmallGroup,
  SmallGroupActivity,
  SmallGroupActivityEvaluation,
  SmallGroupActivityEvaluationPayload,
  SmallGroupActivityPayload,
  SmallGroupActivityPlan,
  SmallGroupActivityPlanPayload,
  SmallGroupPayload,
} from '../types/smallGroup';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchSmallGroups(keyword?: string): Promise<SmallGroup[]> {
  const { data } = await apiClient.get<SmallGroup[]>('/small-groups', {
    params: keyword?.trim() ? { q: keyword.trim() } : undefined,
  });
  return data;
}

export async function fetchSmallGroup(groupId: number): Promise<SmallGroup> {
  const { data } = await apiClient.get<SmallGroup>(`/small-groups/${groupId}`);
  return data;
}

export async function fetchNextSmallGroupNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/small-groups/suggestions/group-no'
  );
  return data;
}

export async function createSmallGroup(payload: SmallGroupPayload): Promise<SmallGroup> {
  const { data } = await apiClient.post<SmallGroup>('/small-groups', payload);
  return data;
}

export async function updateSmallGroup(
  groupId: number,
  payload: SmallGroupPayload
): Promise<SmallGroup> {
  const { data } = await apiClient.patch<SmallGroup>(`/small-groups/${groupId}`, payload);
  return data;
}

export async function deleteSmallGroup(groupId: number): Promise<void> {
  await apiClient.delete(`/small-groups/${groupId}`);
}

export async function approveSmallGroup(groupId: number): Promise<SmallGroup> {
  const { data } = await apiClient.post<SmallGroup>(`/small-groups/${groupId}/approve`);
  return data;
}

export async function revokeSmallGroup(groupId: number): Promise<SmallGroup> {
  const { data } = await apiClient.post<SmallGroup>(`/small-groups/${groupId}/revoke`);
  return data;
}

export async function fetchSmallGroupPlans(groupId?: number): Promise<SmallGroupActivityPlan[]> {
  const { data } = await apiClient.get<SmallGroupActivityPlan[]>('/small-group-activity-plans', {
    params: groupId != null ? { groupId } : undefined,
  });
  return data;
}

export async function fetchSmallGroupPlan(planId: number): Promise<SmallGroupActivityPlan> {
  const { data } = await apiClient.get<SmallGroupActivityPlan>(
    `/small-group-activity-plans/${planId}`
  );
  return data;
}

export async function fetchNextSmallGroupPlanNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/small-group-activity-plans/suggestions/plan-no'
  );
  return data;
}

export async function createSmallGroupPlan(
  payload: SmallGroupActivityPlanPayload
): Promise<SmallGroupActivityPlan> {
  const { data } = await apiClient.post<SmallGroupActivityPlan>(
    '/small-group-activity-plans',
    payload
  );
  return data;
}

export async function updateSmallGroupPlan(
  planId: number,
  payload: SmallGroupActivityPlanPayload
): Promise<SmallGroupActivityPlan> {
  const { data } = await apiClient.patch<SmallGroupActivityPlan>(
    `/small-group-activity-plans/${planId}`,
    payload
  );
  return data;
}

export async function deleteSmallGroupPlan(planId: number): Promise<void> {
  await apiClient.delete(`/small-group-activity-plans/${planId}`);
}

export async function approveSmallGroupPlan(planId: number): Promise<SmallGroupActivityPlan> {
  const { data } = await apiClient.post<SmallGroupActivityPlan>(
    `/small-group-activity-plans/${planId}/approve`
  );
  return data;
}

export async function revokeSmallGroupPlan(planId: number): Promise<SmallGroupActivityPlan> {
  const { data } = await apiClient.post<SmallGroupActivityPlan>(
    `/small-group-activity-plans/${planId}/revoke`
  );
  return data;
}

export async function fetchSmallGroupActivities(groupId?: number): Promise<SmallGroupActivity[]> {
  const { data } = await apiClient.get<SmallGroupActivity[]>('/small-group-activities', {
    params: groupId != null ? { groupId } : undefined,
  });
  return data;
}

export async function fetchSmallGroupActivity(activityId: number): Promise<SmallGroupActivity> {
  const { data } = await apiClient.get<SmallGroupActivity>(
    `/small-group-activities/${activityId}`
  );
  return data;
}

export async function fetchNextSmallGroupRecordNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/small-group-activities/suggestions/record-no'
  );
  return data;
}

export async function createSmallGroupActivity(
  payload: SmallGroupActivityPayload
): Promise<SmallGroupActivity> {
  const { data } = await apiClient.post<SmallGroupActivity>('/small-group-activities', payload);
  return data;
}

export async function updateSmallGroupActivity(
  activityId: number,
  payload: SmallGroupActivityPayload
): Promise<SmallGroupActivity> {
  const { data } = await apiClient.patch<SmallGroupActivity>(
    `/small-group-activities/${activityId}`,
    payload
  );
  return data;
}

export async function deleteSmallGroupActivity(activityId: number): Promise<void> {
  await apiClient.delete(`/small-group-activities/${activityId}`);
}

export async function approveSmallGroupActivity(
  activityId: number
): Promise<SmallGroupActivity> {
  const { data } = await apiClient.post<SmallGroupActivity>(
    `/small-group-activities/${activityId}/approve`
  );
  return data;
}

export async function revokeSmallGroupActivity(
  activityId: number
): Promise<SmallGroupActivity> {
  const { data } = await apiClient.post<SmallGroupActivity>(
    `/small-group-activities/${activityId}/revoke`
  );
  return data;
}

export async function fetchSmallGroupEvaluations(
  groupId?: number
): Promise<SmallGroupActivityEvaluation[]> {
  const { data } = await apiClient.get<SmallGroupActivityEvaluation[]>(
    '/small-group-activity-evaluations',
    { params: groupId != null ? { groupId } : undefined }
  );
  return data;
}

export async function fetchSmallGroupEvaluation(
  evaluationId: number
): Promise<SmallGroupActivityEvaluation> {
  const { data } = await apiClient.get<SmallGroupActivityEvaluation>(
    `/small-group-activity-evaluations/${evaluationId}`
  );
  return data;
}

export async function fetchNextSmallGroupEvaluationNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/small-group-activity-evaluations/suggestions/evaluation-no'
  );
  return data;
}

export async function createSmallGroupEvaluation(
  payload: SmallGroupActivityEvaluationPayload
): Promise<SmallGroupActivityEvaluation> {
  const { data } = await apiClient.post<SmallGroupActivityEvaluation>(
    '/small-group-activity-evaluations',
    payload
  );
  return data;
}

export async function updateSmallGroupEvaluation(
  evaluationId: number,
  payload: SmallGroupActivityEvaluationPayload
): Promise<SmallGroupActivityEvaluation> {
  const { data } = await apiClient.patch<SmallGroupActivityEvaluation>(
    `/small-group-activity-evaluations/${evaluationId}`,
    payload
  );
  return data;
}

export async function deleteSmallGroupEvaluation(evaluationId: number): Promise<void> {
  await apiClient.delete(`/small-group-activity-evaluations/${evaluationId}`);
}

export async function approveSmallGroupEvaluation(
  evaluationId: number
): Promise<SmallGroupActivityEvaluation> {
  const { data } = await apiClient.post<SmallGroupActivityEvaluation>(
    `/small-group-activity-evaluations/${evaluationId}/approve`
  );
  return data;
}

export async function revokeSmallGroupEvaluation(
  evaluationId: number
): Promise<SmallGroupActivityEvaluation> {
  const { data } = await apiClient.post<SmallGroupActivityEvaluation>(
    `/small-group-activity-evaluations/${evaluationId}/revoke`
  );
  return data;
}


export async function downloadSmallGroupPdf(
  groupId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/small-groups/${groupId}/pdf`,
    fileName || `소집단_${groupId}.pdf`
  );
}

export async function downloadSmallGroupPlanPdf(
  planId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/small-group-activity-plans/${planId}/pdf`,
    fileName || `소집단활동계획_${planId}.pdf`
  );
}

export async function downloadSmallGroupActivityPdf(
  activityId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/small-group-activities/${activityId}/pdf`,
    fileName || `소집단활동_${activityId}.pdf`
  );
}

export async function downloadSmallGroupEvaluationPdf(
  evaluationId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/small-group-activity-evaluations/${evaluationId}/pdf`,
    fileName || `소집단활동평가_${evaluationId}.pdf`
  );
}
